import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useDispatch, useSelector } from 'react-redux';
import { getBoxesTemplates } from '../../stores/slices/fourBoxesTemplates/fourBoxesTemplatesActions';
import { selectBoxTemplate } from '../../stores/slices/goals/goalsActions';
import Card from '../Card';

const Modal4BoxesTemplate = ({ isOpen, onClose }) => {
  const { boxTemplates, listLoading } = useSelector(
    state => state.boxTemplates
  );
  const { isLoading } = useSelector(state => state.goals);

  const dispatch = useDispatch();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const cancelRef = useRef();
  const {
    isOpen: isOpenAlertOverride,
    onOpen: onOpenAlertOverride,
    onClose: onCloseAlertOverride,
  } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    dispatch(getBoxesTemplates());
    // eslint-disable-next-line
  }, []);

  const onSaveTemplate = async () => {
    const res = await dispatch(selectBoxTemplate({ id: selectedTemplate.id }));

    if (res.type === 'boxTemplate/selectBoxTemplate/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      onClose();

      onCloseAlertOverride();
      setSelectedTemplate(null);
      toast({
        description: 'New template has been used',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
  };

  return (
    <React.Fragment>
      <Modal onClose={onClose} isOpen={isOpen} blockScrollOnMount={false}>
        <ModalOverlay />
        <ModalContent maxW={'6xl'}>
          <ModalHeader>Select template</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {listLoading ? (
              <Spinner color="primary" />
            ) : boxTemplates.length > 0 ? (
              <Stack
                w={'full'}
                h={'full'}
                spacing={{ base: 0, xl: 4 }}
                direction={'row'}
              >
                <Box
                  w={'200px'}
                  overflow="hidden"
                  borderRight={'1px'}
                  borderColor={'gray.500'}
                  pr={'2'}
                >
                  {boxTemplates.map(template => (
                    <Button
                      variant={'outline'}
                      key={template.id}
                      color={'black'}
                      mb={'2'}
                      size={'sm'}
                      w={'full'}
                      justifyContent={'start'}
                      whiteSpace={'initial'}
                      textAlign={'left'}
                      h={'auto'}
                      minH={'max-content'}
                      py={'1'}
                      onClick={() => setSelectedTemplate(template)}
                      isActive={
                        selectedTemplate
                          ? template.id === selectedTemplate.id
                          : false
                      }
                      _active={{ background: 'primary', color: 'white' }}
                    >
                      {template.name}
                    </Button>
                  ))}
                </Box>
                <Box w={'full'} overflowX={'hidden'} overflowY={'auto'}>
                  {selectedTemplate ? (
                    <SimpleGrid
                      columns={{ base: 1, lg: 2 }}
                      spacingX={2}
                      spacingY={{ base: 2, lg: 2 }}
                      w={'full'}
                    >
                      {selectedTemplate.goal_category_templates.map(
                        category => {
                          return (
                            <Card
                              key={category.id}
                              px={2}
                              py={1}
                              border={'1px solid'}
                              borderColor={'lightGray.500'}
                            >
                              <VStack w={'full'} spacing={2} h={'200px'}>
                                <HStack
                                  direction={'row'}
                                  justifyContent={'center'}
                                  alignItems={'center'}
                                  position="relative"
                                  w={'full'}
                                >
                                  <Text
                                    textAlign={'center'}
                                    fontWeight={'bold'}
                                    fontSize={'sm'}
                                    w="full"
                                  >
                                    {category.title !== ''
                                      ? category.title
                                      : '[Please enter category title]'}
                                  </Text>
                                </HStack>
                                <Scrollbars>
                                  <Flex
                                    overflowY="auto"
                                    flexDirection="column"
                                    flex={1}
                                    w={'full'}
                                  >
                                    {category.goal_templates.length > 0 ? (
                                      category.goal_templates.map((goal, i) => (
                                        <HStack
                                          px={'3'}
                                          bg={
                                            i % 2 === 0
                                              ? 'secondary'
                                              : 'transparent'
                                          }
                                          justifyContent={'space-between'}
                                          key={goal.id}
                                        >
                                          <Text
                                            w={'full'}
                                            fontSize={'14px'}
                                            py={4}
                                          >
                                            {goal.text}
                                          </Text>
                                        </HStack>
                                      ))
                                    ) : (
                                      <Text>No template found.</Text>
                                    )}
                                  </Flex>
                                </Scrollbars>
                              </VStack>
                            </Card>
                          );
                        }
                      )}
                    </SimpleGrid>
                  ) : (
                    <Text>Please select a template.</Text>
                  )}
                </Box>
              </Stack>
            ) : (
              <Text>No Templates found.</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              mr={3}
              onClick={onOpenAlertOverride}
              disabled={selectedTemplate === null}
              isLoading={isLoading}
            >
              Select
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <AlertDialog
        isOpen={isOpenAlertOverride}
        leastDestructiveRef={cancelRef}
        onClose={onCloseAlertOverride}
        blockScrollOnMount={false}
        closeOnOverlayClick={isLoading ? false : true}
        closeOnEsc={isLoading ? false : true}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Select from template?
            </AlertDialogHeader>

            <AlertDialogBody>
              Choosing a template will override your changes. Proceed to
              applying this template?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                variant={'outline'}
                onClick={onCloseAlertOverride}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                bg={'red.400'}
                _hover={{
                  color: 'red.400',
                  bg: 'transparent',
                }}
                onClick={() => {
                  onSaveTemplate();
                }}
                isLoading={isLoading}
                ml={3}
              >
                Okay
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </React.Fragment>
  );
};

export default Modal4BoxesTemplate;
